import { formatHealAmount } from './combat-text';

/**
 * **Um `+TOTAL` por nave, somado em milli** (gate V28G.I, decisão final).
 *
 * ## O que isto substitui, e porquê
 *
 * O leque de seis posições. Ele existia para separar números simultâneos da
 * mesma nave, e a folha visual provou que não chega: o passo do leque é de sete
 * píxeis e o glifo mede cerca de dez, portanto sete curas do mesmo casco liam-se
 * como «há muitos números» em vez de sete números.
 *
 * A regra nova não separa: **não deixa haver mais do que um.** Enquanto o texto
 * de uma nave estiver vivo, cada cura nova soma-se ao total dele e reescreve-o.
 * Zero sobreposição por construção, e não por afinação de offsets.
 *
 * ## O que ela deliberadamente NÃO faz
 *
 * Não reinicia coisa nenhuma. A duração continua a ser os 580 ms que começaram
 * na primeira cura, a posição é onde o texto nasceu, e a escala, o alpha e os
 * tweens seguem o seu curso. Uma atualização toca no TEXTO e mais nada - se
 * tocasse na animação, uma nave a curar sem parar teria um número imortal, e um
 * número imortal é outro efeito visual.
 *
 * Quando os 580 ms acabam o texto desaparece, e a cura seguinte abre um período
 * novo com um total novo.
 *
 * ## A geração, e o que ela impede
 *
 * Cada período recebe um número. O `onComplete` do tween leva o seu consigo e só
 * liberta a entrada se ela ainda for a dele.
 *
 * Sem isso havia uma corrida real: o texto de uma nave expira, o `onComplete`
 * fica agendado, uma cura nova cria o período seguinte, e o callback velho
 * chega depois e apaga o texto novo - deixando a nave sem número e o mapa a
 * apontar para um objeto reciclado. É a mesma classe de defeito que
 * `clearDamageTexts()` tinha, e resolve-se do mesmo modo: o dono verifica a
 * identidade antes de largar.
 *
 * ## Porque é genérico em `T`
 *
 * Porque a regra não sabe o que é um `Phaser.GameObjects.Text`. As duas cenas
 * passam o que quiserem e recebem-no de volta; um teste passa um objeto simples
 * e prova a regra inteira sem um browser. É a mesma razão por que
 * `heal-numbers.ts` não conhece uma cena.
 */

/** Uma nave com texto vivo: o objeto, o total até agora, e o período. */
export interface HealTallySlot<T> {
  readonly text: T;
  /** O total do período, em MILLI inteiro. Nunca em vírgula flutuante. */
  readonly milli: number;
  /** Qual período este objeto serve. */
  readonly generation: number;
}

/** O que a cena tem de desenhar depois de uma cura entrar. */
export interface HealTallyUpdate<T> {
  readonly text: T;
  /** `true` na primeira cura do período: a cena cria os tweens. */
  readonly created: boolean;
  readonly milli: number;
  /** Já formatado a partir do total: `+1`, `+2,52`, `+0,09`. */
  readonly label: string;
  readonly generation: number;
}

export class HealTally<T> {
  private readonly slots = new Map<string, { text: T; milli: number; generation: number }>();
  private nextGeneration = 1;

  /**
   * Soma uma cura ao total da nave, criando o período se não houver um.
   *
   * `create` só é chamado quando é mesmo preciso um objeto novo - a cena não
   * tira nada do pool para depois o devolver.
   *
   * Devolve `null` quando não há nada a desenhar: uma cura de zero, negativa ou
   * não finita não é um facto, e um total que o formatador recusa não vira
   * `+0`.
   */
  add(attacker: string, milli: number, create: () => T): HealTallyUpdate<T> | null {
    if (!Number.isFinite(milli) || milli <= 0) {
      return null;
    }
    const found = this.slots.get(attacker);
    if (found) {
      /* A soma acontece em MILLI inteiro: é isso que a torna exata. */
      found.milli += milli;
      const label = formatHealAmount(found.milli);
      if (label === null) {
        return null;
      }
      return {
        text: found.text,
        created: false,
        milli: found.milli,
        label,
        generation: found.generation,
      };
    }
    const label = formatHealAmount(milli);
    if (label === null) {
      return null;
    }
    const generation = this.nextGeneration;
    this.nextGeneration += 1;
    const text = create();
    this.slots.set(attacker, { text, milli, generation });
    return { text, created: true, milli, label, generation };
  }

  /**
   * O período acabou. Devolve o texto a reciclar, ou `null`.
   *
   * `null` quando a entrada já não é deste período - o texto que lá está agora
   * pertence a uma cura posterior, e largá-lo apagaria um número que o jogador
   * está a ver.
   */
  expire(attacker: string, generation: number): T | null {
    const found = this.slots.get(attacker);
    if (!found || found.generation !== generation) {
      return null;
    }
    this.slots.delete(attacker);
    return found.text;
  }

  /** Tudo o que está vivo, para uma fronteira o reciclar de uma vez. */
  takeAll(): readonly T[] {
    const texts = [...this.slots.values()].map((slot) => slot.text);
    this.slots.clear();
    return texts;
  }

  /** Quantas naves têm número neste instante. */
  get size(): number {
    return this.slots.size;
  }

  /** O período de uma nave, para uma prova o poder ler. */
  peek(attacker: string): HealTallySlot<T> | null {
    const found = this.slots.get(attacker);
    return found ? { text: found.text, milli: found.milli, generation: found.generation } : null;
  }
}
